
import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getIconByName, iconMapping } from "@/utils/iconMapping";

interface IconInputProps {
  value: string;
  onChange: (value: string) => void;
}

const IconInput: React.FC<IconInputProps> = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState(value);
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState("");
  const [availableIcons, setAvailableIcons] = useState<string[]>([]);
  
  // Load available icons on component mount
  useEffect(() => {
    const iconNames = Object.keys(iconMapping)
      .filter(name => !name.includes(".") && !name.toLowerCase().includes("lucide") && name.charAt(0) === name.charAt(0).toUpperCase())
      .sort();
    
    // Remove duplicates from case-insensitive mapping
    const uniqueNames = Array.from(new Set(iconNames));
    setAvailableIcons(uniqueNames.slice(0, 10)); // Show first 10 icons as examples
    
    console.log("[IconInput] Loaded", uniqueNames.length, "unique icon names");
  }, []);
  
  // Check if the icon exists in our mapping
  const iconExists = (iconName: string): boolean => {
    return !!getIconByName(iconName);
  };

  // Get the icon component if it exists
  const getIconComponent = () => {
    const iconName = inputValue.trim();
    if (!iconName) return null;
    
    const IconComponent = getIconByName(iconName);
    if (IconComponent) {
      return React.createElement(IconComponent, { className: "h-5 w-5" });
    }
    
    return <AlertCircle className="h-5 w-5 text-destructive" />;
  };

  // Validate the input whenever it changes
  useEffect(() => {
    const iconName = inputValue.trim();
    if (!iconName) {
      setIsValid(false);
      setError("Icon name cannot be empty");
      return;
    }
    
    if (!iconExists(iconName)) {
      setIsValid(false);
      setError(`Icon "${iconName}" not found in Lucide. Please try one of: ${availableIcons.join(", ")}`);
      return;
    }
    
    setIsValid(true);
    setError("");
    onChange(iconName);
  }, [inputValue, onChange, availableIcons]);

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label htmlFor="iconName">Icon Name</Label>
          <Input
            id="iconName"
            placeholder="Enter Lucide icon name (e.g., Home, Baby, Activity)"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className={!isValid ? "border-destructive" : ""}
          />
        </div>
        <div className="h-10 w-10 flex items-center justify-center border rounded-md bg-background">
          {getIconComponent()}
        </div>
      </div>
      
      {!isValid && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <p className="text-xs text-muted-foreground">
        Available icons include: {availableIcons.join(", ")}, and many more.
        Visit <a href="https://lucide.dev/" target="_blank" rel="noreferrer" className="underline">lucide.dev</a> for the full list of icons.
        Use exactly the same capitalization as shown on the website.
      </p>
    </div>
  );
};

export default IconInput;
